import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { CalendarArrowDown, ChevronLeft, ChevronRight, ChevronRight as ChevronForward, ExternalLink, RefreshCcw } from 'lucide-react';
import { useQuickReminderComposer } from '@/components/QuickReminderComposer';
import { useCalendarItems, type CalendarItem } from '@/hooks/useCalendarItems';
import { generateIcs, downloadIcs, type IcsEvent } from '@/lib/ics-export';
import { syncAdultingCalendarItems, isGoogleCalendarConfigured } from '@/lib/google-calendar';
import {
  DEFAULT_INTEGRATION_SETTINGS,
  loadIntegrationSettings,
  saveIntegrationSettings,
  type IntegrationSettingsRecord,
} from '@/lib/integration-settings';
import { useToast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppLoadingState,
  AppPage,
  AppPageIntro,
  AppSectionHeader,
  AppText,
} from '../../ui-foundation';
import { AppPanelButton, AppWebButton } from '../../ui-foundation/adapters/web';

function getTimingLabel(daysUntil: number) {
  if (daysUntil < 0) {
    const overdueBy = Math.abs(daysUntil);
    return overdueBy === 1 ? '1 day overdue' : `${overdueBy} days overdue`;
  }
  if (daysUntil === 0) return 'Today';
  if (daysUntil === 1) return 'Tomorrow';
  return `In ${daysUntil} days`;
}

function getTimingClasses(daysUntil: number) {
  if (daysUntil < 0) return 'bg-red-100 text-red-800';
  if (daysUntil <= 7) return 'bg-amber-100 text-amber-800';
  return 'bg-green-100 text-green-800';
}

function groupLabel(date: string) {
  return format(parseISO(date), 'EEEE d MMMM');
}

function formatCalendarSyncStatus(lastSyncedAt: string | null, syncStatus: string | null | undefined) {
  if (syncStatus === 'error') {
    return lastSyncedAt
      ? `Sync needs another try. Previous sync ${new Date(lastSyncedAt).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}`
      : 'Sync needs another try';
  }

  if (!lastSyncedAt) {
    return 'Not synced yet';
  }

  return `Last synced ${new Date(lastSyncedAt).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function AgendaItem({ item, onOpen }: { item: CalendarItem; onOpen: (path: string) => void }) {
  return (
    <AppPanelButton
      type="button"
      onClick={() => onOpen(item.path)}
      className="flex items-center gap-3 bg-card"
    >
      <span className="text-2xl shrink-0">{item.emoji}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {item.sectionLabel}
        </p>
        <p className="text-sm font-semibold text-foreground truncate">{item.title}</p>
        <p className="text-xs text-muted-foreground">{item.subtitle}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${getTimingClasses(item.daysUntil)}`}>
          {getTimingLabel(item.daysUntil)}
        </span>
        <ChevronForward size={16} className="text-muted-foreground" />
      </div>
    </AppPanelButton>
  );
}

function calendarItemToIcs(item: CalendarItem): IcsEvent {
  return {
    uid: `adulting-${item.id}@adulting.app`,
    summary: `${item.emoji} ${item.title}`,
    description: item.subtitle ? `${item.sectionLabel} — ${item.subtitle}` : item.sectionLabel,
    dtstart: item.date,
    categories: item.sectionLabel,
  };
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { openQuickReminder } = useQuickReminderComposer();
  const { toast } = useToast();
  const { user } = useApp();
  const { items, loading, overdueItems, upcomingItems, nextSevenDaysCount } = useCalendarItems();
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettingsRecord | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [syncingGoogleCalendar, setSyncingGoogleCalendar] = useState(false);
  const externalCalendarItems = useMemo(
    () => items.filter((item) => item.source !== 'home-routine' || item.externalSyncEnabled !== false),
    [items],
  );

  const usingGoogleCalendar = integrationSettings?.calendar_mode === 'google_calendar';
  const usingAppleCalendar = integrationSettings?.calendar_mode === 'icloud';

  const fetchIntegrationSettings = useCallback(async () => {
    if (!user) {
      setIntegrationSettings(null);
      setSettingsLoading(false);
      return;
    }

    setSettingsLoading(true);
    try {
      const settings = await loadIntegrationSettings(user.id);
      setIntegrationSettings(settings);
    } catch (error) {
      console.error(error);
      setIntegrationSettings({
        user_id: user.id,
        ...DEFAULT_INTEGRATION_SETTINGS,
      });
    } finally {
      setSettingsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchIntegrationSettings();
  }, [fetchIntegrationSettings]);

  useEffect(() => {
    const refresh = () => {
      void fetchIntegrationSettings();
    };

    window.addEventListener('adulting:integration-settings-changed', refresh);
    return () => window.removeEventListener('adulting:integration-settings-changed', refresh);
  }, [fetchIntegrationSettings]);

  function handleExportIcs() {
    const upcoming = externalCalendarItems.filter((item) => item.daysUntil >= 0 && item.daysUntil <= 365);
    const overdueToExport = externalCalendarItems.filter((item) => item.daysUntil < 0);
    const allToExport = [...overdueToExport, ...upcoming];

    if (allToExport.length === 0) {
      toast({ title: 'Nothing to export', description: 'Add some dates to your Adulting sections first.' });
      return;
    }

    const icsEvents: IcsEvent[] = allToExport.map(calendarItemToIcs);
    const icsContent = generateIcs(icsEvents, 'Adulting Life Admin');
    downloadIcs(icsContent, 'adulting-calendar.ics');
    toast({
      title: 'Calendar exported',
      description: `${allToExport.length} events exported. Open the .ics file to add them to your calendar app.`,
    });
  }

  const handleGoogleCalendarSync = async () => {
    if (!user) return;

    if (!isGoogleCalendarConfigured()) {
      toast({
        title: 'Google Calendar is not configured',
        description: 'This build still needs the Google web client ID before Calendar sync can run.',
        variant: 'destructive',
      });
      return;
    }

    setSyncingGoogleCalendar(true);

    try {
      const result = await syncAdultingCalendarItems(externalCalendarItems, integrationSettings?.calendar_external_id || null);
      const syncedAt = new Date().toISOString();

      const nextSettings = await saveIntegrationSettings(user.id, {
        calendar_mode: 'google_calendar',
        calendar_external_id: result.calendarId,
        calendar_external_name: result.calendarName,
        calendar_external_url: result.calendarUrl,
        calendar_last_synced_at: syncedAt,
        calendar_sync_status: 'synced',
        google_account_email: result.userEmail || integrationSettings?.google_account_email || null,
      });

      setIntegrationSettings(nextSettings);
      toast({
        title: 'Google Calendar synced',
        description: `${result.createdCount} added, ${result.updatedCount} updated, ${result.deletedCount} removed.`,
      });
    } catch (error) {
      console.error(error);
      if (user) {
        try {
          const nextSettings = await saveIntegrationSettings(user.id, {
            calendar_sync_status: 'error',
          });
          setIntegrationSettings(nextSettings);
        } catch (saveError) {
          console.error(saveError);
        }
      }

      toast({
        title: 'Calendar sync failed',
        description: error instanceof Error ? error.message : 'Could not sync your Google Calendar.',
        variant: 'destructive',
      });
    } finally {
      setSyncingGoogleCalendar(false);
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1;

  const itemsByDate = useMemo(() => {
    return items.reduce<Record<string, CalendarItem[]>>((acc, item) => {
      if (!acc[item.date]) acc[item.date] = [];
      acc[item.date].push(item);
      return acc;
    }, {});
  }, [items]);

  const selectedItems = useMemo(
    () => items.filter((item) => isSameDay(parseISO(item.date), selectedDate)),
    [items, selectedDate],
  );

  const nextThirtyDays = useMemo(
    () => upcomingItems.filter((item) => item.daysUntil <= 30).slice(0, 12),
    [upcomingItems],
  );

  const groupedUpcoming = useMemo(() => {
    return nextThirtyDays.reduce<Record<string, CalendarItem[]>>((acc, item) => {
      if (!acc[item.date]) acc[item.date] = [];
      acc[item.date].push(item);
      return acc;
    }, {});
  }, [nextThirtyDays]);

  if (loading) {
    return (
      <AppPage className="animate-fade-in">
        <AppLoadingState
          title="Loading your calendar"
          body="Pulling dates across family, home, health, work, and documents."
        />
      </AppPage>
    );
  }

  return (
    <AppPage className="animate-fade-in">
      <AppPageIntro
        subtitle="Every important date in one place, across family, home, health, documents and work."
        action={(
          <AppButtonRow>
            <AppButton size="sm" onPress={() => openQuickReminder()}>
              Brain Dump
            </AppButton>
            <AppButton tone="outline" size="sm" onPress={handleExportIcs}>
              <CalendarArrowDown size={14} />
              Export ICS
            </AppButton>
          </AppButtonRow>
        )}
      />

      <AppCard>
        <div className="flex items-start gap-3">
          <RefreshCcw size={18} className="mt-0.5 shrink-0 text-primary" />
          <div className="flex-1 min-w-0">
            <AppText fontSize="$md" fontWeight="700">
              {settingsLoading
                ? 'Loading calendar sync…'
                : usingGoogleCalendar
                  ? 'Google Calendar sync is enabled'
                  : usingAppleCalendar
                    ? 'Apple Calendar sync is enabled'
                  : 'Adulting calendar is active'}
            </AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
              {usingGoogleCalendar
                ? 'Adulting still keeps the source calendar view here, and the same dates can now be pushed into your own Google Calendar across devices.'
                : usingAppleCalendar
                  ? 'Adulting still keeps the source calendar view here. Apple Calendar sync is handled in the Adulting iPhone app, where the same dates can be pushed into your Apple calendar across devices.'
                  : 'Adulting keeps the main calendar here across your Adulting devices. Use ICS export any time, switch Calendar to Google Calendar on web, or use Apple Calendar sync in the iPhone app when you want a live synced copy in your own calendar too.'}
            </AppText>

            {usingGoogleCalendar || usingAppleCalendar ? (
              <AppCard backgroundColor="$background" className="mt-3">
                <AppText fontSize="$xs" fontWeight="700" color="$colorMuted" textTransform="uppercase" letterSpacing={1}>
                  Sync status
                </AppText>
                <AppText fontSize="$sm">
                  {integrationSettings?.calendar_external_name || 'Adulting LifeOS'}
                  {usingGoogleCalendar && integrationSettings?.google_account_email ? ` · ${integrationSettings.google_account_email}` : ''}
                </AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                  {formatCalendarSyncStatus(
                    integrationSettings?.calendar_last_synced_at || null,
                    integrationSettings?.calendar_sync_status,
                  )}
                </AppText>
                <AppButtonRow className="mt-2">
                  {usingGoogleCalendar ? (
                    <AppButton size="sm" onPress={() => void handleGoogleCalendarSync()} disabled={syncingGoogleCalendar}>
                      <RefreshCcw size={12} className={syncingGoogleCalendar ? 'animate-spin' : ''} />
                      {syncingGoogleCalendar ? 'Syncing…' : 'Sync now'}
                    </AppButton>
                  ) : (
                    <AppButton size="sm" onPress={() => navigate('/profile')}>
                      Open iPhone instructions
                    </AppButton>
                  )}
                  {usingGoogleCalendar && integrationSettings?.calendar_external_url ? (
                    <AppButton
                      tone="outline"
                      size="sm"
                      onPress={() => window.open(integrationSettings.calendar_external_url || '', '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink size={12} />
                      Open Google Calendar
                    </AppButton>
                  ) : null}
                  <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
                    Manage in My Settings
                  </AppButton>
                </AppButtonRow>
              </AppCard>
            ) : (
              <AppButton className="mt-3" tone="outline" size="sm" onPress={() => navigate('/profile')}>
                Manage in My Settings
              </AppButton>
            )}
          </div>
        </div>
      </AppCard>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <AppCard>
          <AppText fontSize="$xs" fontWeight="700" color="$colorMuted" textTransform="uppercase" letterSpacing={1}>Needs action</AppText>
          <AppText fontSize="$display" fontWeight="700">{String(overdueItems.length)}</AppText>
        </AppCard>
        <AppCard>
          <AppText fontSize="$xs" fontWeight="700" color="$colorMuted" textTransform="uppercase" letterSpacing={1}>Next 7 days</AppText>
          <AppText fontSize="$display" fontWeight="700">{String(nextSevenDaysCount)}</AppText>
        </AppCard>
        <AppCard>
          <AppText fontSize="$xs" fontWeight="700" color="$colorMuted" textTransform="uppercase" letterSpacing={1}>Tracked dates</AppText>
          <AppText fontSize="$display" fontWeight="700">{String(items.length)}</AppText>
        </AppCard>
      </div>

      <AppCard>
        <AppSectionHeader
          title={format(currentMonth, 'MMMM yyyy')}
          subtitle="All tracked dates in one monthly view."
          action={(
            <AppButtonRow>
              <AppButton
                tone="ghost"
                size="sm"
                onPress={() => {
                  const nextMonth = subMonths(currentMonth, 1);
                  setCurrentMonth(nextMonth);
                  setSelectedDate(startOfMonth(nextMonth));
                }}
              >
                <ChevronLeft size={16} />
              </AppButton>
              <AppButton
                tone="ghost"
                size="sm"
                onPress={() => {
                  const nextMonth = addMonths(currentMonth, 1);
                  setCurrentMonth(nextMonth);
                  setSelectedDate(startOfMonth(nextMonth));
                }}
              >
                <ChevronRight size={16} />
              </AppButton>
            </AppButtonRow>
          )}
        />

        <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground mb-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: startDay }).map((_, index) => (
            <div key={`empty-${index}`} />
          ))}
          {days.map((day) => {
            const key = format(day, 'yyyy-MM-dd');
            const dayItems = itemsByDate[key] || [];
            const isSelected = isSameDay(day, selectedDate);

            return (
              <AppWebButton
                key={key}
                type="button"
                variant="ghost"
                onClick={() => setSelectedDate(day)}
                className={[
                  'relative h-11 flex-col items-center justify-center rounded-lg border text-xs font-medium transition-colors',
                  isSelected ? 'border-primary bg-primary/10 text-primary' : 'border-transparent text-foreground hover:bg-muted/60',
                  isToday(day) ? 'ring-1 ring-primary/30' : '',
                  !isSameMonth(day, currentMonth) ? 'text-muted-foreground/40' : '',
                ].join(' ')}
              >
                <span>{day.getDate()}</span>
                {dayItems.length > 0 && (
                  <span className="absolute bottom-1 inline-flex min-w-[16px] justify-center rounded-full bg-secondary/10 px-1.5 py-0.5 text-[9px] font-semibold text-secondary">
                    {dayItems.length}
                  </span>
                )}
              </AppWebButton>
            );
          })}
        </div>
      </AppCard>

      <AppCard>
        <AppSectionHeader
          title={format(selectedDate, 'EEEE d MMMM')}
          subtitle="Dates already tracked against the selected day."
          action={selectedItems.length > 0 ? (
            <AppButton tone="ghost" size="sm" onPress={() => navigate(selectedItems[0].path)}>
              Open section
            </AppButton>
          ) : null}
        />

        {selectedItems.length === 0 ? (
          <AppEmptyState
            title="Nothing tracked for this day"
            body="Use Family, Home, Health or Work to add dates that should show here."
          />
        ) : (
          <div className="space-y-2">
            {selectedItems.map((item) => (
              <AgendaItem key={item.id} item={item} onOpen={navigate} />
            ))}
          </div>
        )}
      </AppCard>

      <AppCard>
        <AppSectionHeader
          title="Next 30 days"
          subtitle={`${nextThirtyDays.length} visible`}
        />

        {nextThirtyDays.length === 0 ? (
          <AppEmptyState
            title="No upcoming dates in the next month"
            body="Add dates in Family, Home, Health, Work, or Documents to keep this view useful."
          />
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedUpcoming).map(([date, dayItems]) => (
              <div key={date}>
                <AppText className="mb-2" fontSize="$sm" fontWeight="700" color="$colorMuted">
                  {groupLabel(date)}
                </AppText>
                <div className="space-y-2">
                  {dayItems.map((item) => (
                    <AgendaItem key={item.id} item={item} onOpen={navigate} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </AppCard>
    </AppPage>
  );
}
